import React, { useMemo } from 'react';
import ReactFlow, { MarkerType, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import QuestionNode from '../flow/QuestionNode';
import ResponseEdge from '../flow/ResponseEdge';

// Use the same node/edge types as the real FlowChartEditor
const nodeTypes = { question: QuestionNode };
const edgeTypes = { response: ResponseEdge };

/**
 * Simple dagre layout for when no saved positions exist.
 */
const computeDagreLayout = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 100, ranksep: 180 });

  nodes.forEach((node) => {
    g.setNode(node.id, { width: 350, height: 260 });
  });
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    return { ...node, position: { x: pos.x - 175, y: pos.y - 70 } };
  });
};

/**
 * Get the opening node + its direct children (exactly 2 rows).
 * Row 1: opening node
 * Row 2: all nodes the opening node's responses lead to
 */
const getTwoLayerNodes = (graph) => {
  if (!graph?.nodes) return new Set();
  
  const nodes = graph.nodes;
  const nodeIds = Object.keys(nodes).filter(
    id => id && typeof id === 'string' && !id.startsWith('objection_obj_') && !id.startsWith('objection_')
  );

  // Find opening node
  let startId = nodeIds.find(id => nodes[id]?.isOpeningNode);
  if (!startId) {
    const referenced = new Set();
    nodeIds.forEach(id => {
      const node = nodes[id];
      if (node?.responses) {
        Object.values(node.responses).forEach(resp => {
          if (resp?.nextNode && resp.nextNode !== '__end_script__') referenced.add(resp.nextNode);
        });
      }
    });
    startId = nodeIds.find(id => !referenced.has(id)) || nodeIds[0];
  }

  if (!startId) return new Set();

  const visible = new Set();
  visible.add(startId);

  // Add all direct children of the opening node (row 2)
  const startNode = nodes[startId];
  if (startNode?.responses) {
    Object.values(startNode.responses).forEach(resp => {
      if (resp.nextNode && resp.nextNode !== '__end_script__' && nodes[resp.nextNode] && !resp.nextNode.startsWith('objection_')) {
        visible.add(resp.nextNode);
      }
    });
  }

  return visible;
};


/**
 * MiniFlowchartPreview - Real flowchart preview using actual QuestionNode and ResponseEdge
 * 
 * Renders a read-only, zoomed-out view showing the opening node (row 1) and all its
 * direct children (row 2) — exactly 2 layers of the flowchart.
 */
const MiniFlowchartPreviewInner = ({ 
  graph,
  steps: stepsProp,
  onOpenFlowchart,
}) => {
  const { nodes, edges, nodeCount } = useMemo(() => {
    if (!graph?.nodes) return { nodes: [], edges: [], nodeCount: 0 };

    const graphNodes = graph.nodes;
    const steps = stepsProp || [];

    // Build step name lookup
    const stepNameMap = {};
    steps.forEach(step => { stepNameMap[step.id] = step.name || step.id; });

    // Get opening node + its direct children (2 rows)
    const visibleNodeIds = getTwoLayerNodes(graph);
    if (visibleNodeIds.size === 0) return { nodes: [], edges: [], nodeCount: 0 };

    // Build ReactFlow nodes — same format as graphToReactFlow in FlowChartEditor
    const reactNodes = [];
    visibleNodeIds.forEach((nodeId) => {
      const nodeData = graphNodes[nodeId];
      if (!nodeData) return;
      const stepName = stepNameMap[nodeData.step] || nodeData.step;

      reactNodes.push({
        id: nodeId,
        type: 'question',
        position: { x: 0, y: 0 }, // placeholder — dagre will position
        data: {
          ...nodeData,
          id: nodeId,
          title: nodeId,
          snippet: typeof nodeData.script === 'string' ? nodeData.script.slice(0, 90) : 'Custom script',
          isOpeningNode: nodeData.isOpeningNode,
          stepName: stepName,
          onEdit: null,
          onDelete: null,
        },
      });
    });

    // Build edges — only between visible nodes
    const reactEdges = [];
    visibleNodeIds.forEach((sourceId) => {
      const nodeData = graphNodes[sourceId];
      if (nodeData?.responses) {
        Object.entries(nodeData.responses).forEach(([key, response]) => {
          if (response.nextNode && response.nextNode !== '__end_script__' && visibleNodeIds.has(response.nextNode)) {
            reactEdges.push({
              id: `${sourceId}-${response.nextNode}-${key}`,
              source: sourceId,
              target: response.nextNode,
              type: 'response',
              data: {
                label: response.label || key,
                responseKey: key,
                isEditing: false,
              },
              sourceHandle: 'out-bottom',
              targetHandle: 'in-top',
              markerEnd: { type: MarkerType.ArrowClosed },
            });
          }
        });
      }
    });

    // Always use dagre auto-layout for a clean, consistent preview
    const finalNodes = computeDagreLayout(reactNodes, reactEdges);

    // Total node count (all non-objection nodes)
    const totalCount = Object.keys(graphNodes).filter(
      id => id && !id.startsWith('objection_obj_') && !id.startsWith('objection_')
    ).length;

    return { nodes: finalNodes, edges: reactEdges, nodeCount: totalCount };
  }, [graph, stepsProp]);

  if (!graph?.nodes || nodeCount === 0) return null;

  return (
    <div 
      className="mb-4 rounded-lg bg-white shadow-sm overflow-hidden"
      style={{ position: 'relative', cursor: 'pointer', border: '1.5px solid #a78bfa' }}
      role="button"
      tabIndex={0}
      aria-label="Open flowchart editor"
      onClick={() => onOpenFlowchart && onOpenFlowchart()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenFlowchart && onOpenFlowchart(); } }}
    >
      {/* Top-left button */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          borderRadius: '7px',
          background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
          color: 'white',
          fontSize: '12px',
          fontWeight: 600,
          boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
        }}>
          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Open Flowchart Editor
        </div>
        <span style={{ fontSize: '11px', color: '#6b7280', background: 'rgba(255,255,255,0.85)', padding: '3px 8px', borderRadius: '5px' }}>
          {nodeCount} nodes
        </span>
      </div>

      {/* ReactFlow container — real QuestionNode components */}
      <div style={{ height: '340px', width: '100%', pointerEvents: 'none' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 0.7 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#fafbfc' }}
        />
      </div>
    </div>
  );
};

/**
 * Wrap in ReactFlowProvider so the ReactFlow instance works standalone.
 */
const MiniFlowchartPreview = (props) => {
  if (!props.graph?.nodes || Object.keys(props.graph.nodes).length === 0) return null;

  return (
    <ReactFlowProvider>
      <MiniFlowchartPreviewInner {...props} />
    </ReactFlowProvider>
  );
};

export default MiniFlowchartPreview;
